import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API } from '../init'
import { getItemFromLS } from '../cache'

export const CONTACT_VERIFICATION_TOKEN_KEY = 'contact-verification-token'
export const CONTACT_VERIFICATION_ENTRY_TOKEN_KEY = 'contact-verification-entry-token'
export const CONTACT_VERIFICATION_EMAIL_VALIDATED_KEY = 'contact-verification-email-validated'
export const CONTACT_VERIFICATION_PHONE_VALIDATED_KEY = 'contact-verification-phone-validated'
export const CONTACT_VERIFICATION_VAT_KEY = 'contact-verification-vat'
export const CONTACT_VERIFICATION_EMAIL_KEY = 'contact-verification-email'
export const CONTACT_VERIFICATION_PHONE_KEY = 'contact-verification-phone'
export const CONTACT_VERIFICATION_IDENTITY_DATA_KEY = 'contact-verification-identity-data'
export const CONTACT_VERIFICATION_PROCESS_STATUS_KEY = 'contact-verification-process-status'

function authConfig(token) {
  return { headers: { Authorization: `Bearer ${token || getItemFromLS(CONTACT_VERIFICATION_TOKEN_KEY) || ''}` } }
}

function unwrap(response) {
  return response.data?.data
}

const startMutationFn = async ({ verificationToken, vat }) => {
  if (!API_URL) {
    throw new Error('API_URL not configured')
  }

  const session = unwrap(await API.post('/api/v1/public/verification/start', { vat }, authConfig(verificationToken)))

  if (!session?.token) {
    throw new Error('Could not start verification process')
  }

  return session
}

export function useStartContactVerificationProcess() {
  return useMutation({ mutationFn: startMutationFn })
}

export function useContactVerificationData(enabled = true) {
  return useQuery({
    queryKey: ['contact-verification', 'contact-data'],
    queryFn: async () => unwrap(await API.get('/api/v1/public/verification/contact-data', authConfig())),
    enabled,
    retry: false,
  })
}

export function useSaveContactVerificationData() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ contactData, proofOfAddress }) => {
      const formData = new FormData()

      Object.entries(contactData).forEach(([key, value]) => {
        formData.append(key, value ?? '')
      })
      if (proofOfAddress) {
        formData.append('proofOfAddress', proofOfAddress)
      }

      return unwrap(
        await API.put('/api/v1/public/verification/contact-data', formData, {
          ...authConfig(),
          headers: {
            ...authConfig().headers,
            'Content-Type': 'multipart/form-data',
          },
        }),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-verification', 'contact-data'] })
    },
  })
}

export function useCompleteContactVerificationProcess() {
  return useMutation({
    mutationFn: async (payload) => unwrap(await API.post('/api/v1/public/verification/complete', payload, authConfig())),
  })
}

export function useRequestContactVerificationEmailToken() {
  return useMutation({
    mutationFn: async ({ email }) => unwrap(await API.post('/api/v1/public/verification/email-token', { email }, authConfig())),
  })
}

export function useValidateContactVerificationEmailToken() {
  return useMutation({
    mutationFn: async ({ token }) => unwrap(await API.post('/api/v1/public/verification/email-token/validate', { token }, authConfig())),
  })
}

export function useRequestContactVerificationPhoneToken() {
  return useMutation({
    mutationFn: async ({ phone }) => unwrap(await API.post('/api/v1/public/verification/phone-token', { phone }, authConfig())),
  })
}

export function useValidateContactVerificationPhoneToken() {
  return useMutation({
    mutationFn: async ({ token }) => unwrap(await API.post('/api/v1/public/verification/phone-token/validate', { token }, authConfig())),
  })
}
